import { useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { ArrowUpRight, Eye, ExternalLink, Globe, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/empty-state';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/skeleton';
import { DefinitionList, DefinitionRow } from '@/components/definition-list';
import { PublishCelebration } from '@/components/publish-celebration';
import { CreatePageModal } from '@/components/create-page-modal';
import { SiteSeoAudit } from '@/components/site-seo-audit';
import { ExternalWebsiteCard } from '@/components/external-website-card';
import { DomainPanel } from '@/components/domain-panel';
import { UpgradeGate } from '@/components/upgrade-gate';
import { RouteTabs } from '@/components/route-tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMe, invalidateMe } from '@/hooks/use-me';
import { api } from '@/lib/api';
import { readyPrimaryVariant } from '@/lib/primary-action';
import { locationRoutes } from '@/lib/route-map';
import type {
  PageSummary,
  SiteDetailResponse,
  SiteMutationResponse,
  SiteStatus,
} from '@/lib/types';

// =====================================================================
// Website — WP1 Task 7 move. Interior of the old site-detail.tsx (pages
// list, launch/pause/archive, preview, password protection, SEO health
// card, external-website card), minus the 9-chip cross-nav toolbar (now
// PanelShell's sidebar) and minus the "AI & indexing" llms.txt/AI-crawler
// card (moved verbatim to foundation/get-found-ready.tsx). The AEO audit
// card and the review-link/QR card are deliberately NOT here — they move in
// Tasks 8 and 9 respectively, from site-detail.tsx's old render.
//
// Domain renders as a nested tab of this same screen at
// locationRoutes.websiteDomain(businessId, siteId) ("/domain") — same component, mounted twice
// in main.tsx, switching on the current path.
// =====================================================================

const STATUS_BADGES: Record<
  SiteStatus,
  { label: string; variant: 'success' | 'muted' | 'warning' | 'destructive' | 'outline' }
> = {
  draft: { label: 'Draft', variant: 'muted' },
  published: { label: 'Live', variant: 'success' },
  paused: { label: 'Paused', variant: 'warning' },
  archived: { label: 'Archived', variant: 'destructive' },
  demo: { label: 'Demo', variant: 'outline' },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function Website() {
  const meState = useMe();
  const { businessId = '', locationId: siteId } = useTenantParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState<SiteDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gate, setGate] = useState<{ capability: string; planKey: 'pro' } | null>(null);
  const [busy, setBusy] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [createPageOpen, setCreatePageOpen] = useState(false);
  const [deletingPageId, setDeletingPageId] = useState<string | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const onDomainTab = siteId ? location.pathname === locationRoutes.websiteDomain(businessId, siteId) : false;

  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    let cancelled = false;
    api
      .get<SiteDetailResponse>(locationApi(businessId, siteId, ''))
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <PageError
        eyebrow="Foundation"
        title={onDomainTab ? 'Domain' : 'Website'}
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready') {
    return null; // shell renders its own skeleton on /app
  }

  if (error && !data) {
    return (
      <EmptyState
        title="Couldn't load this site"
        description={error}
        action={
          <Link to="/app">
            <Button variant="outline">Back to dashboard</Button>
          </Link>
        }
      />
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const { site, pages: sitePages, passwordProtectionAvailable } = data;
  const badge = STATUS_BADGES[site.status];
  const isPublished = site.status === 'published';
  // Design audit T8 (A11, one primary per screen). "New page" is the Website
  // tab's primary action — it was an outline button while the page's only
  // filled control sat in the header. But Launch is the bigger, rarer,
  // once-per-site primary: while a site is still in development mode the
  // header owns the screen's single filled button, and "New page" steps back
  // to outline. Once the site is live, Launch is gone and "New page" is it.
  const showsLaunch = site.status === 'paused' || site.status === 'draft';

  // ONE "New page" button, rendered in one of two places (design audit T10/
  // A12, mirroring the surveys-list pattern): above the list when there is a
  // list, or inside the empty state when there isn't. Two copies of it would
  // be two primaries on a live site, which is exactly what T8 removed.
  const newPageButton = (
    <Button
      variant={showsLaunch ? 'outline' : 'default'}
      size="sm"
      onClick={() => setCreatePageOpen(true)}
    >
      <Plus className="h-4 w-4" />
      New page
    </Button>
  );
  const hasPages = sitePages.length > 0;

  // Pause / return-to-dev-mode only — ungated, owner-driven (site-lifecycle
  // #19, Task 1). Going live is a separate, paid-gated act: launch() below.
  async function setStatus(status: 'draft' | 'paused') {
    if (!siteId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.patch<SiteMutationResponse>(locationApi(businessId, siteId, ''), { status });
      setData((prev) => (prev ? { ...prev, site: result.site } : prev));
      setShowCelebration(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // Launch — the ONE door from draft/paused → published (site-lifecycle #19,
  // Task 1). Paid-tier gated server-side; POST /api/sites/:id/launch.
  async function launch() {
    if (!siteId) return;
    setBusy(true);
    setError(null);
    setGate(null);
    try {
      const result = await api.post<SiteMutationResponse>(locationApi(businessId, siteId, 'launch'));
      setData((prev) => (prev ? { ...prev, site: result.site } : prev));
      setShowCelebration(true);
    } catch (err) {
      const e = err as Error & { body?: { error?: string } };
      if (e.body?.error === 'launch_requires_paid_plan') {
        setGate({ capability: 'Launching a site requires a paid plan.', planKey: 'pro' });
      } else if (e.body?.error === 'not_launchable') {
        setError("This site can't be launched right now.");
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  }

  // Site-lifecycle #19, Task 3 — set/clear the shared site-wide password.
  // Server-re-sync-not-optimistic: local state reflects what actually landed
  // in D1, and reverts itself on a failed write instead of showing a state
  // that was never persisted.
  async function setSitePassword(passcode: string | null) {
    if (!siteId) return;
    setPasswordBusy(true);
    setPasswordError(null);
    try {
      const result = await api.patch<SiteMutationResponse>(locationApi(businessId, siteId, ''), { passcode });
      setData((prev) => (prev ? { ...prev, site: result.site } : prev));
      setPasswordInput('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : String(err));
    } finally {
      setPasswordBusy(false);
    }
  }

  async function handlePreview() {
    if (!siteId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.get<{ url?: string; disabled?: boolean }>(
        locationApi(businessId, siteId, 'preview-url'),
      );
      if (res.url) {
        window.open(res.url, '_blank', 'noopener');
      } else {
        setError('Live preview isn’t enabled yet.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function deletePage(page: PageSummary) {
    if (!siteId) return;
    const ok = window.confirm(
      `Delete "${page.title}"? The page and everything on it goes away. Other pages on this site are untouched.`,
    );
    if (!ok) return;
    setDeletingPageId(page.id);
    setError(null);
    try {
      await api.delete(locationApi(businessId, siteId, `pages/${page.id}`));
      setData((prev) =>
        prev ? { ...prev, pages: prev.pages.filter((p) => p.id !== page.id) } : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingPageId(null);
    }
  }

  async function archive() {
    if (!siteId) return;
    const ok = window.confirm(
      "Archive this site? Visitors won't be able to see it, but you can bring it back any time.",
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(locationApi(businessId, siteId, ''));
      invalidateMe();
      navigate('/app');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // A raw code must never reach a customer surface. `purge_incomplete` =
      // a large Search Console history is still being cleared; the next
      // Archive press resumes where this one stopped.
      setError(
        message === 'purge_incomplete'
          ? 'We are still clearing this site\'s Search Console history — press Archive again to finish.'
          : message,
      );
      setBusy(false);
    }
  }

  return (
    <PageContainer>
      {showCelebration && isPublished ? (
        <PublishCelebration
          publicUrl={site.publicUrl}
          onDismiss={() => setShowCelebration(false)}
        />
      ) : null}

      {/* The h1 is the PAGE name, and it follows the active tab — Website
          and Domain are two pages of one screen (design audit B1). The
          business name moved to the sidebar switcher; the live URL and the
          publish badge stay here, where they say something about THIS page. */}
      <PageHeader
        eyebrow="Foundation"
        title={onDomainTab ? 'Domain' : 'Website'}
        description={
          <a
            href={site.publicUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-6 items-center gap-1 font-mono text-sm text-muted-foreground transition-colors hover:text-foreground pointer-coarse:min-h-11"
          >
            {site.publicUrl.replace(/^https?:\/\//, '')}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        }
        actions={
          <>
            <Badge variant={badge.variant}>{badge.label}</Badge>
            <Button variant="outline" disabled={busy} onClick={handlePreview}>
              <Eye className="h-4 w-4" />
              Preview
            </Button>
            {isPublished ? (
              <Button variant="outline" disabled={busy} onClick={() => setStatus('paused')}>
                Pause
              </Button>
            ) : null}
            {showsLaunch ? (
              <Button disabled={busy} onClick={() => launch()}>
                <Globe className="h-4 w-4" />
                Launch
              </Button>
            ) : null}
          </>
        }
      />

      {gate ? (
        <UpgradeGate capability={gate.capability} planKey={gate.planKey} from={location.pathname} />
      ) : error ? (
        <div className="rounded-xl border bg-danger-bg px-4 py-3 text-sm text-danger-fg">
          {error}
        </div>
      ) : null}

      {/* Website | Domain tab strip (Domain is a nested tab of this same
          screen, per WP1 Task 7 — Domain mounts existing components/
          domain-panel.tsx below when this route is routes.websiteDomain). */}
      <RouteTabs
        ariaLabel="Website sections"
        activeKey={onDomainTab ? 'domain' : 'website'}
        tabs={[
          { key: 'website', label: 'Website', to: siteId ? locationRoutes.website(businessId, siteId) : '#' },
          { key: 'domain', label: 'Domain', to: siteId ? locationRoutes.websiteDomain(businessId, siteId) : '#' },
        ]}
      />

      {onDomainTab ? (
        siteId ? <DomainPanel siteId={siteId} primaryClaimed={showsLaunch} /> : null
      ) : (
        <>
          <section className="space-y-4">
            {/* While the list is empty the EmptyState below is the only voice
                in this section (fix round 1): its own "No pages yet" heading
                and sentence say everything this header would, and stacking the
                two gave the owner two headings and two sentences about the
                same nothing. */}
            {hasPages ? (
              <header className="flex items-end justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight">Pages</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Open a page to edit. Add new pages for About, Services, Contact, and anything
                    else.
                  </p>
                </div>
                {newPageButton}
              </header>
            ) : null}
            {/* B8 asked for `as="h3"` here, on the theory that this sits under
                the Pages <h2>. It does not: the header above is rendered ONLY
                when `hasPages`, so when this shows, the section has no other
                heading and this IS the h2. Left as the default, deliberately. */}
            {!hasPages ? (
              <EmptyState
                size="compact"
                title="No pages yet"
                description="Add your first page and the editor opens straight onto it. Most owners start with Home, then add About, Services and Contact as they go."
                action={newPageButton}
              />
            ) : (
              <ul className="divide-y divide-border-subtle rounded-xl border bg-card">
                {sitePages.map((p: PageSummary) => {
                  const isHome = p.slug === 'home';
                  const isDeleting = deletingPageId === p.id;
                  return (
                    <li
                      key={p.id}
                      className="group flex items-center gap-2 px-4 py-3 transition-colors hover:bg-muted/40"
                    >
                      <Link
                        to={siteId ? locationRoutes.pageEdit(businessId, siteId, p.id) : '#'}
                        className="flex flex-1 items-center justify-between gap-4 min-w-0"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{p.title}</div>
                          <div className="truncate font-mono text-xs text-muted-foreground">
                            /{p.slug}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* "Live" must mean the page is ACTUALLY publicly
                              reachable — a published page on a site that has
                              never been launched is not (fix wave, 2026-08-10
                              final review, Finding 7; same defect already fixed
                              in page-edit.tsx, same labels used here). */}
                          <Badge
                            variant={
                              p.published && isPublished
                                ? 'success'
                                : p.published
                                  ? 'warning'
                                  : 'muted'
                            }
                            title={
                              p.published && !isPublished
                                ? 'Saved — launch the site to make it public'
                                : undefined
                            }
                          >
                            {p.published && isPublished ? 'Live' : p.published ? 'Saved' : 'Draft'}
                          </Badge>
                          <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                        </div>
                      </Link>
                      {isHome ? null : (
                        <button
                          type="button"
                          aria-label={`Delete ${p.title}`}
                          title="Delete page"
                          disabled={isDeleting}
                          onClick={() => void deletePage(p)}
                          className="rounded-md p-1.5 text-text-tertiary opacity-0 transition-[color,background-color,opacity] hover:bg-danger-bg hover:text-danger-fg focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 group-hover:opacity-100 disabled:opacity-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <CreatePageModal
            open={createPageOpen}
            siteId={site.id}
            onClose={() => setCreatePageOpen(false)}
            onCreated={(pageId) => {
              setCreatePageOpen(false);
              navigate(locationRoutes.pageEdit(businessId, site.id, pageId));
            }}
          />

          <SiteSeoAudit siteId={site.id} />

          <ExternalWebsiteCard siteId={site.id} primaryClaimed={showsLaunch} />

          <section className="space-y-4">
            <header className="flex items-end justify-between gap-4">
              <h2 className="text-lg font-semibold tracking-tight">Details</h2>
              <Button variant="ghost" size="sm" disabled={busy} onClick={archive}>
                <Trash2 className="h-4 w-4" />
                Archive
              </Button>
            </header>
            <DefinitionList>
              <DefinitionRow term="URL slug" mono>
                /{site.slug}
              </DefinitionRow>
              <DefinitionRow term="Template" mono>
                {site.template}
              </DefinitionRow>
              <DefinitionRow term="Created">{formatDate(site.createdAt)}</DefinitionRow>
              <DefinitionRow term="Last updated">{formatDate(site.updatedAt)}</DefinitionRow>
              {site.publishedAt ? (
                <DefinitionRow term="First published">
                  {formatDate(site.publishedAt)}
                </DefinitionRow>
              ) : null}
            </DefinitionList>
          </section>

          {/* Disabled-safe (fix wave, 2026-08-10 final review, Finding 5): with no
              SITE_PASSWORD_SECRET on the workers, the renderer fails CLOSED — a
              protected site serves a 503 to every visitor. So when the feature
              isn't available the card hides itself entirely (same precedent as
              the Preview button hiding without PREVIEW_TOKEN_SECRET) — UNLESS a
              password is somehow already on, in which case the owner needs the
              honest explanation and the Remove button to get their site back. */}
          {!passwordProtectionAvailable && !site.requiresPasscode ? null : (
            <section className="space-y-4 rounded-lg border border-border-subtle bg-card p-6">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">Password protection</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  One shared password for the whole site. It keeps casual visitors out — not
                  bank-grade security — and search engines are told to skip the site while it&rsquo;s
                  on.
                </p>
              </div>
              {!passwordProtectionAvailable ? (
                <p className="rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-fg">
                  This site has a password on, but password protection isn&rsquo;t running right now,
                  so visitors can&rsquo;t get in at all. Remove the password below to make the site
                  reachable again, or contact support and we&rsquo;ll sort it out.
                </p>
              ) : null}
              {site.requiresPasscode ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-surface-sunken px-3 py-2.5">
                  <span className="text-sm font-medium text-foreground">
                    Password protection is on
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={passwordBusy}
                    onClick={() => void setSitePassword(null)}
                  >
                    Remove password
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="flex flex-1 flex-col gap-1.5 sm:max-w-xs">
                    <Label htmlFor="site-passcode">Password</Label>
                    <Input
                      id="site-passcode"
                      type="text"
                      autoComplete="off"
                      placeholder="At least 4 characters"
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                    />
                  </div>
                  {/* Design audit T8 (A11): outline until the field holds a
                      usable password, filled once it does. A permanently
                      disabled filled button is the exact "disabled primary"
                      the rule bans — this one only turns primary at the
                      moment it can actually do something, and not even then
                      while Launch is on screen (T14). */}
                  <Button
                    type="button"
                    variant={readyPrimaryVariant(passwordInput.trim().length >= 4, showsLaunch)}
                    disabled={passwordBusy || passwordInput.trim().length < 4}
                    onClick={() => void setSitePassword(passwordInput.trim())}
                  >
                    Set password
                  </Button>
                </div>
              )}
              {passwordBusy ? <p className="text-xs text-muted-foreground">Saving…</p> : null}
              {passwordError ? (
                <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
                  {passwordError}
                </p>
              ) : null}
            </section>
          )}
        </>
      )}
    </PageContainer>
  );
}
